import { useCallback, useSyncExternalStore } from 'react';

function subscribeToMediaQuery(query: string, onChange: () => void) {
    const mql = globalThis.matchMedia?.(query);
    mql?.addEventListener('change', onChange);
    return () => mql?.removeEventListener('change', onChange);
}

function getMediaQuerySnapshot(query: string) {
    return globalThis.matchMedia?.(query)?.matches ?? false;
}

/**
 * Subscribe a component to a single CSS **media** query. Uses {@link useSyncExternalStore} so
 * the result is correct after hydration and updates on viewport / preference changes
 * (resize, `matchMedia` / `addEventListener("change", …)` on the `MediaQueryList`).
 *
 * @param query — valid media query string, e.g. `(min-width: 768px)`.
 * @param getServerSnapshot — value during SSR and before client hydration. Defaults to
 *   `() => false` (mobile-first). Override if you need a different server snapshot.
 */
export function useMediaQuery(query: string, getServerSnapshot: () => boolean = () => false) {
    const subscribe = useCallback((onChange: () => void) => subscribeToMediaQuery(query, onChange), [query]);
    const getSnapshot = useCallback(() => getMediaQuerySnapshot(query), [query]);
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
